import React, { CSSProperties, FC, ReactNode } from 'react';
import * as SwiperReact from 'swiper/react';
import styled from './Swiper.scss';
import { AutoplayOptions } from 'swiper/types/modules/autoplay';
import { LazyOptions } from 'swiper/types/modules/lazy';
import { Autoplay, Controller, Lazy, Navigation, Pagination, Scrollbar } from 'swiper';
import { NavigationOptions, SwiperModule, Swiper as SwiperInterface } from 'swiper/types';
import { PaginationOptions } from 'swiper/types/modules/pagination';
import { ScrollbarOptions } from 'swiper/types/modules/scrollbar';
import 'swiper/css/bundle';
import { IWithStylesProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';

export interface ISwiper extends SwiperInterface {}
export interface ISlides {
  id: string;
  content?: ReactNode;
}

export interface ISwiperProps extends IWithStylesProps {
  /** Массив слайдов */
  slides: ISlides[];

  /** Ориентация */
  direction?: 'horizontal' | 'vertical';

  /** Кол-во слайдов, отображаемых за раз */
  slidesPerView?: number | 'auto';

  /** Расстояние между слайдами */
  spaceBetween?: number;

  /** Начальный слайд */
  initialSlide?: number;

  /** Следовать на слайд при клике на него? */
  slideToClickedSlide?: boolean;

  /** Зацикленность карусели */
  loop?: boolean;

  /** Отображение навигационных кнопок (слева, справа) */
  navigation?: boolean | NavigationOptions;

  /** Отображение точек под каруселью */
  pagination?: boolean | PaginationOptions;

  /** Отображение бага, показывающего положение карусели */
  scrollbar?: boolean | ScrollbarOptions;

  /** Автоматическое воспроизведение */
  autoplay?: boolean | AutoplayOptions;

  /** Отложенная загрузка слайдов */
  lazy?: boolean | LazyOptions;

  /** Событие создания экземпляра свайпера */
  onSwiper?: (swiper: ISwiper) => void;

  /** Событие смены слайдов */
  onSlideChange?: (swiper: ISwiper) => void;

  /** Событие окончания анимации смены слайдов */
  onSlideChangeTransitionEnd?: (swiper: ISwiper) => void;

  slideStyle?: CSSProperties;

  slideClasses?: string;
}

export const Swiper: FC<ISwiperProps> = ({
  slides,
  className,
  style,
  slideStyle,
  slideClasses: slideClassesProps,
  direction = 'horizontal',
  slidesPerView = 1,
  ...rest
}) => {
  const { navigation, pagination, scrollbar, autoplay, lazy } = rest;
  let modules = [Controller] as SwiperModule[];

  if (navigation) {
    modules = [...modules, Navigation];
  }

  if (pagination) {
    modules = [...modules, Pagination];
  }

  if (scrollbar) {
    modules = [...modules, Scrollbar];
  }

  if (autoplay) {
    modules = [...modules, Autoplay];
  }

  if (lazy) {
    modules = [...modules, Lazy];
  }

  const classes = classnames(styled.root, className);

  return (
    <div className={classes} style={style}>
      <SwiperReact.Swiper
        className={styled.swiper}
        modules={modules}
        slidesPerView={slidesPerView}
        direction={direction}
        {...rest}
      >
        {slides.length > 0 &&
          slides.map((slide) => {
            return (
              <SwiperReact.SwiperSlide style={slideStyle} className={slideClassesProps} key={slide.id}>
                {slide.content}
              </SwiperReact.SwiperSlide>
            );
          })}
      </SwiperReact.Swiper>
    </div>
  );
};
